import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchContactsAsync,
  fetchNewContactsCountAsync,
  updateContactStatusAsync,
  deleteContactAsync,
  clearError,
  setFilters,
  clearFilters,
  setPage,
  Contact,
} from '../store/slices/contactsSlice';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import Select, { SelectOption } from './Select';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';
import './Contacts.css';

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  processing: 'Processing',
  invalid: 'Invalid',
  consultation: 'Consultation',
};

const STATUS_VALUES: Contact['status'][] = ['new', 'processing', 'invalid', 'consultation'];

const ROW_STATUS_OPTIONS: SelectOption<Contact['status']>[] =
  STATUS_VALUES.map(s => ({ value: s, label: STATUS_LABELS[s] }));

const FILTER_STATUS_OPTIONS: SelectOption<string>[] = [
  { value: 'all', label: 'All Statuses' },
  ...ROW_STATUS_OPTIONS,
];

const Contacts: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { contacts, loading, error, pagination, filters } = useAppSelector((state) => state.contacts);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [statusFilter, setStatusFilter] = useState(filters.status);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'status' | 'delete' | null;
    contactId: number | null;
    newValue: Contact['status'] | null;
    currentValue: Contact['status'] | null;
  }>({
    isOpen: false,
    type: null,
    contactId: null,
    newValue: null,
    currentValue: null,
  });

  const loadContacts = useCallback(() => {
    dispatch(fetchContactsAsync({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      search: filters.search,
      status: filters.status,
    }));
  }, [dispatch, pagination.currentPage, pagination.itemsPerPage, filters]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleSearch = useCallback(() => {
    dispatch(setFilters({
      search: searchInput.trim(),
      status: statusFilter,
    }));
  }, [dispatch, searchInput, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setStatusFilter('all');
    dispatch(clearFilters());
  }, [dispatch]);

  const handlePageChange = useCallback((page: number) => {
    dispatch(setPage(page));
  }, [dispatch]);

  const handleStatusChange = (contactId: number, currentStatus: Contact['status'], newStatus: Contact['status']) => {
    setConfirmModal({
      isOpen: true,
      type: 'status',
      contactId,
      newValue: newStatus,
      currentValue: currentStatus,
    });
  };

  const handleDelete = (contactId: number) => {
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      contactId,
      newValue: null,
      currentValue: null,
    });
  };

  const handleConfirmChange = async () => {
    if (!confirmModal.contactId || !confirmModal.type) return;

    try {
      if (confirmModal.type === 'status' && confirmModal.newValue) {
        await dispatch(updateContactStatusAsync({
          id: confirmModal.contactId,
          status: confirmModal.newValue,
        })).unwrap();
        dispatch(fetchNewContactsCountAsync());
      } else if (confirmModal.type === 'delete') {
        await dispatch(deleteContactAsync(confirmModal.contactId)).unwrap();
        dispatch(fetchNewContactsCountAsync());
      }
    } catch (err) {
      console.error('Failed to update:', err);
    }

    setConfirmModal({ isOpen: false, type: null, contactId: null, newValue: null, currentValue: null });
  };

  const handleCancelChange = () => {
    setConfirmModal({ isOpen: false, type: null, contactId: null, newValue: null, currentValue: null });
  };

  const getModalMessage = () => {
    if (confirmModal.type === 'status') {
      const currentLabel = confirmModal.currentValue ? (STATUS_LABELS[confirmModal.currentValue] || confirmModal.currentValue) : '';
      const newLabel = confirmModal.newValue ? (STATUS_LABELS[confirmModal.newValue] || confirmModal.newValue) : '';
      return `Change status from "${currentLabel}" to "${newLabel}"?`;
    } else if (confirmModal.type === 'delete') {
      return 'Are you sure you want to delete this contact?';
    }
    return '';
  };

  if (loading && contacts.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Contacts</h1>
          <p>Manage contact form submissions</p>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <div className="filter-bar">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name or phone..."
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />

        <div className="contacts-status-filter">
          <Select<string, SelectOption<string>>
            value={FILTER_STATUS_OPTIONS.find(o => o.value === statusFilter) || null}
            onChange={(opt) => setStatusFilter(opt ? opt.value : 'all')}
            options={FILTER_STATUS_OPTIONS}
            placeholder="All Statuses"
            isClearable={false}
          />
        </div>

        <button onClick={handleSearch} className="btn-primary">
          Apply
        </button>

        <button onClick={handleClearFilters} className="btn-secondary">
          Clear
        </button>
      </div>

      <div className="results-summary">
        <p>
          Showing {contacts.length} of {pagination.totalItems} contacts
          {(filters.search || filters.status !== 'all') && ' (filtered)'}
        </p>
      </div>

      <div className="students-table-container vocabulary-table">
        {contacts.length === 0 ? (
          <div className="no-students">
            <p>No contacts found.</p>
            {(filters.search || filters.status !== 'all') && (
              <button onClick={handleClearFilters} className="btn-primary">
                Clear filters to see all contacts
              </button>
            )}
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Source</th>
                <th>Level</th>
                <th>Funnel</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id} className={contact.status === 'new' ? 'contact-row-new' : ''}>
                  <td><strong>{contact.name}</strong></td>
                  <td>{contact.phone}</td>
                  <td>{contact.source || '—'}</td>
                  <td>{contact.level || '—'}</td>
                  <td>{contact.funnel?.domain || '—'}</td>
                  <td>
                    <div className="inline-status-select">
                      <Select<Contact['status'], SelectOption<Contact['status']>>
                        value={{ value: contact.status, label: STATUS_LABELS[contact.status] || contact.status }}
                        onChange={(opt) => {
                          if (opt && opt.value !== contact.status) {
                            handleStatusChange(contact.id, contact.status, opt.value);
                          }
                        }}
                        options={ROW_STATUS_OPTIONS}
                        isClearable={false}
                      />
                    </div>
                  </td>
                  <td>{formatDateEuropean(contact.created_at || '')}</td>
                  <td>
                    <div className="action-buttons">
                      {contact.consultation?.id ? (
                        <button
                          onClick={() => navigate(`/consultations/edit/${contact.consultation!.id}`)}
                          className="btn-icon"
                          data-tooltip="View consultation"
                        >
                          📅
                        </button>
                      ) : contact.status !== 'invalid' && (
                        <button
                          onClick={() => navigate(`/consultations/create?fromContact=${contact.id}`)}
                          className="btn-icon"
                          data-tooltip="Schedule consultation"
                          style={{ color: '#059669' }}
                        >
                          +📅
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="btn-icon"
                        data-tooltip="Delete contact"
                        style={{ color: '#e53e3e' }}
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading}
            className="btn-secondary"
          >
            Previous
          </button>

          <div className="pagination-info">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
          </div>

          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {loading && contacts.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.type === 'delete' ? 'Confirm Delete' : 'Confirm Change'}
        message={getModalMessage()}
        onConfirm={handleConfirmChange}
        onCancel={handleCancelChange}
        confirmText={confirmModal.type === 'delete' ? 'Delete' : 'Confirm'}
        cancelText="Cancel"
      />
    </div>
  );
};

export default Contacts;
